import { Link } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';
import type { FooterSectionData, ContactInfoData } from '@/sanity/types';

// R2 - Footer structure as const for type safety
const FOOTER_SECTIONS = [
  {
    title: 'Empresa',
    links: [
      { label: 'Inicio', href: '/' },
      { label: 'Quiénes Somos', href: '/quienes-somos' },
      { label: 'Productos', href: '/productos' },
    ],
  },
  {
    title: 'Contacto',
    links: [
      { label: 'Email: Inbocaconsulting@gmail.com', href: 'mailto:Inbocaconsulting@gmail.com' },
      { label: 'Teléfono: +34 669 375 242', href: 'tel:+34669375242' },
      { label: 'Polígono Romica calle 4/5 bis (Albacete)', href: '#' },
    ],
  },
] as const;

interface FooterProps {
  data?: FooterSectionData | undefined;
  contactInfo?: ContactInfoData | undefined;
}

/**
 * R2 - INBOCA Footer component with company information
 * R4 - Accepts optional Sanity data, falls back to hardcoded defaults
 */
export function Footer({ data, contactInfo }: FooterProps) {
  const companyDesc = data?.companyDescription ?? 'Soluciones corporativas de excelencia con diseño innovador y personalización total.';

  // R4 - Resolve footer link groups from Sanity or use hardcoded defaults
  const resolvedSections = data?.linkGroups?.length
    ? data.linkGroups
    : contactInfo
      ? [
          FOOTER_SECTIONS[0],
          {
            title: 'Contacto',
            links: [
              { label: `Email: ${contactInfo.email}`, href: `mailto:${contactInfo.email}` },
              { label: `Teléfono: ${contactInfo.phone}`, href: `tel:${contactInfo.phoneRaw}` },
              { label: contactInfo.address, href: '#' },
            ],
          },
        ]
      : FOOTER_SECTIONS;
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-b from-inboca-teal/5 to-gray-50 border-t border-gray-200 animate-fade-in">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div className="col-span-1">
            <Link to="/" className="inline-block mb-6" aria-label="INBOCA Home">
              {/* R4: Subtle footer logo with slight opacity */}
              <Logo
                variant="full"
                bg="transparent"
                className="h-8 opacity-80 hover:opacity-100 transition-opacity"
              />
            </Link>
            <p className="text-gray-600 leading-relaxed text-sm">
              {companyDesc}
            </p>
          </div>

          {/* Footer Links */}
          {resolvedSections.map((section) => (
            <div key={section.title}>
              <h4 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith('http') || link.href.startsWith('mailto') || link.href.startsWith('tel') ? (
                      <a
                        href={link.href}
                        className="text-gray-600 hover:text-inboca-teal transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-gray-600 hover:text-inboca-teal transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 pt-8">
          {/* Bottom Footer */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-center sm:text-left">
            <p className="text-gray-600 text-sm">
              Copyright © {currentYear} INBOCA. Todos los derechos reservados.
            </p>
            <div className="flex gap-6 text-sm text-gray-600">
              <a href="#" className="hover:text-inboca-teal transition-colors">
                Política de Privacidad
              </a>
              <a href="#" className="hover:text-inboca-teal transition-colors">
                Términos de Servicio
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
